import type { QueryOptions, UseQueryOptions } from '@zg-rentals/react-query5';
import { useQuery } from '@zg-rentals/react-query5';
import { isEqual, omit } from 'es-toolkit';
import { type ComponentType, createContext, useContext, useEffect, useMemo, useRef } from 'react';

export type AuthProviderQueryOptions<User> = Omit<UseQueryOptions<User>, 'queryKey' | 'queryFn'>;

export type RedirectHref = (href: string, params: URLSearchParams) => string;

/**
 * Create an authentication Provider/Boundary using a React Query options-first API.
 *
 * Callers pass a full `QueryOptions` object instead of a `fetchUser` function;
 * no hidden defaults are applied.
 *
 * See Confluence for details:
 * https://zillowgroup.atlassian.net/wiki/spaces/RP/pages/959381921/auth-react
 *
 * @deprecated Couples server-usable config with React client internals, which
 * breaks Next.js App Router server components. Migrate to `defineConfig`
 * from `@zg-rentals/auth-react` plus `defineClient` from
 * `@zg-rentals/auth-react/client` (and `prefetch` from
 * `@zg-rentals/auth-react/server` for RSC prefetches).
 */
export function defineAuthProvider<Props extends object, User>({
  queryOptions,
  queryKey = ['modules', 'auth-react', 'Auth.Provider'],
  isAuthorized = () => true,
}: {
  /**
   * Return React Query options for fetching the user. The caller controls
   * every aspect of the query (queryKey, queryFn, staleTime, retry, etc.).
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  queryOptions: (props: Props) => QueryOptions<User, Error, User, any>;

  /**
   * Override the query key
   */
  queryKey?: ReadonlyArray<string>;

  /**
   * Determine if user is authorized (after the user is fetched). Default
   * behavior is to always return true.
   */
  isAuthorized?: (user: User, props: Props) => boolean;
}) {
  const Context = createContext<{ options: UseQueryOptions<User>; props: Props } | null>(null);

  const Provider = (props: { children: React.ReactNode } & Props) => {
    const propsRef = useRef(props);
    if (!isEqual(omit(propsRef.current, ['children']), omit(props, ['children']))) {
      propsRef.current = props;
    }
    const stableProps = propsRef.current;

    const options = useMemo(() => queryOptions(stableProps), [stableProps]);
    const value = useMemo(() => ({ options: { ...options, queryKey }, props: stableProps }), [options, stableProps]);
    useQuery(value.options);

    return <Context.Provider value={value}>{props.children}</Context.Provider>;
  };

  const useAuth = () => {
    const context = useContext(Context);
    if (!context) {
      return {
        user: null,
        isAuthorized: false,
        error: null,
        refetch: () => {
          // no-op
        },
      } as const;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { data: user = null, error, ...rest } = useQuery(context.options);

    if (error) {
      return { user: null, isAuthorized: false, error, ...rest } as const;
    }

    if (user && isAuthorized(user, context.props)) {
      return { user, isAuthorized: true, error, ...rest } as const;
    }

    return { user, isAuthorized: false, error, ...rest } as const;
  };

  type BoundaryProps = {
    /**
     * Rendered when user is authorized
     */
    children: React.ReactNode | ((user: User) => React.ReactNode);

    /**
     * Rendered while user is being fetched/authorized
     */
    loading?: React.ReactNode;

    /**
     * Rendered while redirecting to `redirectHref`
     */
    redirecting?: React.ReactNode;

    /**
     * Produces a redirect `onUnauthorized`, use `params` to break redirect loops
     */
    redirectHref?: RedirectHref;

    /**
     * Search param key/value used to detect redirect loops.
     * Only applies when `redirectHref` is set. Defaults to `['ap', 'x']`.
     */
    searchParam?: [key: string, value: string];

    /**
     * Enhance the provider-level `isAuthorized` check for this boundary
     *
     * @note calls both the provider-level `isAuthorized` check and this boundary-level `isAuthorized` check
     */
    isAuthorized?: (user: User) => boolean;

    /**
     * Rendered if user is unauthorized
     *
     * @note If `redirectHref` is provided, this is only called if user is unauthorized after a redirect
     */
    onUnauthorized?: React.ReactNode | ((user: User) => React.ReactNode);

    /**
     * Rendered if there was an error fetching or authorizing user
     */
    onError?: React.ReactNode | ((reason: string) => React.ReactNode);
  };

  const Boundary = ({
    children,
    loading = <>Loading...</>,
    redirecting = <>Redirecting...</>,
    redirectHref,
    searchParam: [searchParamKey, searchParamValue] = ['ap', 'x'],
    isAuthorized: boundaryIsAuthorized = () => true,
    onError,
    onUnauthorized,
  }: BoundaryProps) => {
    const handleError = (message: string) => (typeof onError === 'function' ? onError(message) : (onError ?? message));

    const handleUnauthorized = (unauthorizedUser: User) => {
      if (redirectHref && typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);

        if (params.get(searchParamKey) !== searchParamValue) {
          params.set(searchParamKey, searchParamValue);

          window.location.href = redirectHref(window.location.href.split('?')[0], params);
          return redirecting;
        }
      }

      return typeof onUnauthorized === 'function'
        ? onUnauthorized(unauthorizedUser)
        : (onUnauthorized ?? handleError('User not authorized'));
    };

    const { error, user, isAuthorized } = useAuth();
    const isBoundaryAuthorized = isAuthorized && user != null && boundaryIsAuthorized(user);

    useEffect(() => {
      if (!isBoundaryAuthorized) {
        return;
      }

      const url = new URL(window.location.href);
      if (url.searchParams.get(searchParamKey) === searchParamValue) {
        url.searchParams.delete(searchParamKey);
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      }
    }, [isBoundaryAuthorized, searchParamKey, searchParamValue]);

    if (error) {
      return handleError(error instanceof Error ? error.message : 'Unknown error');
    } else if (isBoundaryAuthorized) {
      return typeof children === 'function' ? children(user) : children;
    } else if (user != null) {
      return handleUnauthorized(user);
    } else {
      return loading;
    }
  };

  const withBoundary = <P extends { user: User }>(
    Component: ComponentType<P>,
    boundaryProps?: Omit<BoundaryProps, 'children'>,
  ) => {
    const Wrapped = (props: Omit<P, 'user'>) => (
      <Boundary {...boundaryProps}>{(user) => <Component {...(props as P)} user={user} />}</Boundary>
    );
    Wrapped.displayName = `withBoundary(${Component.displayName ?? Component.name ?? 'Component'})`;
    return Wrapped;
  };

  return {
    /**
     * Wrap around entire App to provide auth context to children
     */
    Provider,

    /**
     * Wrap around a React sub-tree to ensure that user is logged in and
     * authorized before rendering children
     */
    Boundary,

    /**
     * HOC that wraps a component with a Boundary
     */
    withBoundary,

    /**
     * Access underlying user/authorized state directly
     */
    useAuth,

    /**
     * Helpers for server-side rendering
     */
    queryKey,
    queryOptions: (props: Props): UseQueryOptions<User> => ({ ...queryOptions(props), queryKey }),
    isAuthorized,
  } as const;
}
